import {
  IBillGroupResp,
  IQuoteItemRespSchema,
  IQuoteRespSchema,
  ProductTypeEnum,
  QuoteItemAmendmentStatusEnum,
} from '@monetize/types/app';
import { replaceUserTimezoneWithUtc, toDateShort } from '@monetize/utils/core';
import { isBefore } from 'date-fns/isBefore';
import { subMonths } from 'date-fns/subMonths';
import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';

type QuoteId = string;

type BannerDismissedState = {
  ContractWillBeCancelledBanner: boolean;
  ContractIsCancelledBanner: boolean;
  ContractCancelledFromContractActionBanner: boolean;
  InactiveRateInfoBanner: boolean;
  InactiveRateWarningBanner: boolean;
  RenewalNotAvailableBanner: boolean;
  UsageProductWarningBanner: boolean;
  EarlyRenewalBanner: boolean;
  AcceptedAdminEditableBanner: boolean;
};

// Banner state - whether the banner has been dismissed for a given quote
// atomFamily stores state for a given key, each quote has it's own state stored in memory
const bannerDismissedState = atomFamily((_quoteId: QuoteId) =>
  atom<BannerDismissedState>({
    ContractWillBeCancelledBanner: false,
    ContractIsCancelledBanner: false,
    ContractCancelledFromContractActionBanner: false,
    InactiveRateInfoBanner: false,
    InactiveRateWarningBanner: false,
    RenewalNotAvailableBanner: false,
    UsageProductWarningBanner: false,
    EarlyRenewalBanner: false,
    AcceptedAdminEditableBanner: false,
  }),
);

type BannerParam = [QuoteId, keyof BannerDismissedState];

// Convenience atom to get/set the dismissed state for a given quote+banner.
// Callers pass a fresh tuple on every render, so params are compared by value.
export const bannerDismissedSelector = atomFamily(
  ([quoteId, bannerKey]: BannerParam) =>
    atom(
      (get) => get(bannerDismissedState(quoteId))[bannerKey],
      (_get, set, newValue: boolean) => {
        set(bannerDismissedState(quoteId), (prevValue) => ({
          ...prevValue,
          [bannerKey]: newValue,
        }));
      },
    ),
  ([quoteIdA, bannerKeyA], [quoteIdB, bannerKeyB]) =>
    quoteIdA === quoteIdB && bannerKeyA === bannerKeyB,
);

// Banner messages as constants for maintainability
export const BANNER_MESSAGES = {
  USAGE_ADDITION_WARNING: (date: string) =>
    `Usage products added prior to ${date} will not be invoiced, as invoicing has already progressed beyond this period. Finance can be corrected using a one-time invoice or by performing a credit and re-bill.`,
};

const hasProductTypeInQuoteOffering = (
  items: IQuoteItemRespSchema[],
  productType: ProductTypeEnum,
  amendmentStatus?: QuoteItemAmendmentStatusEnum,
) =>
  items.some(
    ({ productType: pt, amendmentStatus: as }) =>
      pt === productType && (!amendmentStatus || as === amendmentStatus),
  );

/**
 * Checks usage dates and returns banner messages if necessary.
 *
 * @param quote - The quote data.
 * @param billGroup - The billing group data.
 * @returns An array of banner messages.
 */
export const checkUsageDate = (
  quote: IQuoteRespSchema,
  billGroup: IBillGroupResp,
): string[] => {
  if (quote.quoteOfferings.length === 0 || !billGroup.nextInvoiceDate) {
    return [];
  }

  const nextInvoiceDate = replaceUserTimezoneWithUtc(billGroup.nextInvoiceDate);
  const previousInvoiceDate = subMonths(nextInvoiceDate, 1);
  const bannerText = new Set<string>();

  for (const quoteOffering of quote.quoteOfferings) {
    const { items, startDate: startDateStr } = quoteOffering;
    const startDate = replaceUserTimezoneWithUtc(startDateStr);

    const usageProductAdded = hasProductTypeInQuoteOffering(
      items,
      ProductTypeEnum.USAGE,
      QuoteItemAmendmentStatusEnum.ADDED,
    );

    // Check for usage addition/back date warnings
    if (usageProductAdded && isBefore(startDate, previousInvoiceDate)) {
      bannerText.add(
        BANNER_MESSAGES.USAGE_ADDITION_WARNING(
          toDateShort(previousInvoiceDate),
        ),
      );
    }
  }

  return Array.from(bannerText);
};
